import clsx from "clsx";
import {
  ChevronDown,
  CalendarClock,
  House,
  PackageOpen,
  ReceiptText,
  Trophy,
  UserRound,
  Wallet,
  Wifi,
  WifiOff,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link, useLocation } from "wouter";

import { BetSlipDrawer } from "@/betslip/BetSlipDrawer";
import { useBetSlip } from "@/betslip/BetSlipProvider";
import { CashierDialog } from "@/cashier/CashierDialog";
import { useCashier } from "@/cashier/CashierProvider";
import { Button } from "@/components/ui";
import { useRealtime } from "@/realtime/RealtimeProvider";
import { AuthDialog } from "@/session/AuthDialog";
import { useSession } from "@/session/SessionProvider";
import { MonkeyTiltV2Shell } from "@/shell/MonkeyTiltV2Shell";
import { useTheme } from "@/theme/ThemeProvider";

const primaryLinks = [
  { href: "/app/", label: "Home", icon: House },
  { href: "/app/schedule", label: "Schedule", icon: CalendarClock },
  { href: "/app/packs", label: "Packs", icon: PackageOpen },
  { href: "/app/player", label: "Stable", icon: UserRound },
];

function activeFor(location: string, href: string) {
  if (href === "/app/") return location === "/app" || location === "/app/";
  return location.startsWith(href);
}

function NeutralShell({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const { mode } = useRealtime();
  const { session, logout, loginOpen, requestLogin, dismissLogin } =
    useSession();
  const betslip = useBetSlip();
  const cashier = useCashier();
  const { settings, theme } = useTheme();

  const brandName =
    settings?.tenant.name && settings.tenant.name !== "Default Tenant"
      ? settings.tenant.name
      : theme.brand.displayName;

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__inner">
          <Link className="brand" href="/app/">
            <span className="brand__mark">{theme.brand.shortName}</span>
            <span className="brand__label">{brandName}</span>
          </Link>

          <nav className="primary-nav" aria-label="Primary navigation">
            {primaryLinks.map(({ href, label, icon: Icon }) => (
              <Link
                aria-label={label}
                href={href}
                key={href}
                className={clsx(
                  "nav-link",
                  activeFor(location, href) && "nav-link--active",
                )}
              >
                <Icon aria-hidden size={15} />
                <span>{label}</span>
              </Link>
            ))}
            <Link
              aria-label="Leaderboard"
              className={clsx(
                "nav-link",
                location.startsWith("/app/leaderboards") && "nav-link--active",
              )}
              href="/app/leaderboards"
            >
              <Trophy aria-hidden size={15} />
              <span>Leaderboard</span>
            </Link>
          </nav>

          <div className="app-header__actions">
            <button
              aria-controls="betslip-drawer"
              aria-expanded={betslip.open}
              aria-label={`Open Betslip, ${betslip.legs.length} selections`}
              className="betslip-button"
              onClick={(event) => betslip.setOpen(true, event.currentTarget)}
              title={
                cashier.selectedWallet
                  ? [
                      `Cash ${cashier.selectedWallet.cash ?? "unavailable"}`,
                      `Bonus ${cashier.selectedWallet.bonus ?? "unavailable"}`,
                      `Locked ${cashier.selectedWallet.locked ?? "unavailable"}`,
                      cashier.selectedWallet.stale
                        ? "Balance temporarily stale"
                        : "Display-only wallet balance",
                    ].join(" · ")
                  : "Wallet balance unavailable"
              }
              type="button"
            >
              <ReceiptText aria-hidden size={15} />
              <span>Betslip</span>
              {betslip.legs.length ? (
                <span className="betslip-button__count">
                  {betslip.legs.length}
                </span>
              ) : null}
              <strong>
                {cashier.selectedWallet?.stale ? "~" : ""}
                {session ? (cashier.selectedWallet?.total ?? "—") : "—"}
              </strong>
            </button>
            {session ? (
              <>
                <Button
                  onClick={(event) =>
                    cashier.openCashier("deposit", event.currentTarget)
                  }
                  variant="secondary"
                >
                  <Wallet aria-hidden size={15} />
                  <span>Cashier</span>
                </Button>
                <details className="account-menu">
                  <summary>
                    <span className="account-menu__avatar">
                      {session.displayName.slice(0, 1).toUpperCase()}
                    </span>
                    <span>{session.displayName}</span>
                    <ChevronDown aria-hidden size={13} />
                  </summary>
                  <div className="account-menu__panel">
                    <Link href="/app/player">View stable</Link>
                    <button type="button" onClick={() => void logout()}>
                      Sign out
                    </button>
                  </div>
                </details>
              </>
            ) : (
              <Button onClick={requestLogin} variant="secondary">
                Sign in
              </Button>
            )}
            <span
              className={clsx("connection-state", `connection-state--${mode}`)}
              title={`Realtime: ${mode}`}
            >
              {mode === "live" ? (
                <Wifi aria-hidden size={14} />
              ) : (
                <WifiOff aria-hidden size={14} />
              )}
              <span>{mode}</span>
            </span>
          </div>
        </div>
      </header>

      <main className="app-main">{children}</main>

      <footer className="app-footer">
        <span>Every race can be recomputed in your browser.</span>
        <Link href="/app/verify">Fairness verifier</Link>
      </footer>

      <BetSlipDrawer />
      <CashierDialog />
      {loginOpen ? <AuthDialog onClose={dismissLogin} /> : null}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { theme } = useTheme();
  const Shell =
    theme.shellVariant === "monkeytilt-v2" ? MonkeyTiltV2Shell : NeutralShell;
  return <Shell>{children}</Shell>;
}
